export type PuzzleState = {
  n: number;
  board: number[]; // length n*n, 0=blank, 1..n*n-1
  blankIndex: number;
};

export class PuzzleModel {
  private state: PuzzleState;

  constructor(n: number) {
    this.state = this.makeSolved(n);
  }

  getState(): PuzzleState {
    return {
      n: this.state.n,
      board: [...this.state.board],
      blankIndex: this.state.blankIndex,
    };
  }

  resetSolved(n: number) {
    this.state = this.makeSolved(n);
  }

  shuffle(steps: number, rng = Math.random) {
    let lastBlank = -1;
    for (let i = 0; i < steps; i++) {
      const adj = this.adjacentIndices(this.state.blankIndex);
      const filtered = (lastBlank >= 0 && adj.length > 1) ? adj.filter(x => x !== lastBlank) : adj;
      const pick = filtered[Math.floor(rng() * filtered.length)];
      lastBlank = this.state.blankIndex;
      this.swap(pick, this.state.blankIndex);
    }
  }

  tryMove(tileId: number): boolean {
    const tileIndex = this.state.board.indexOf(tileId);
    if (tileIndex < 0) return false;
    if (!this.isAdjacent(tileIndex, this.state.blankIndex)) return false;
    this.swap(tileIndex, this.state.blankIndex);
    return true;
  }

  isSolved(): boolean {
    const { board } = this.state;
    for (let i = 0; i < board.length - 1; i++) {
      if (board[i] !== i + 1) return false;
    }
    return board[board.length - 1] === 0;
  }

  /** 正しい位置にあるタイルの数を返す（空白は含まない） */
  countCorrectTiles(): number {
    const { board } = this.state;
    let count = 0;
    for (let i = 0; i < board.length - 1; i++) {
      if (board[i] === i + 1) count++;
    }
    // 最後のマスが空白かどうかもチェック
    if (board[board.length - 1] === 0) count++;
    return count;
  }

  private makeSolved(n: number): PuzzleState {
    const board: number[] = new Array(n * n);
    for (let i = 0; i < n * n; i++) board[i] = (i === n * n - 1) ? 0 : (i + 1);
    return { n, board, blankIndex: n * n - 1 };
  }

  private isAdjacent(a: number, b: number): boolean {
    const n = this.state.n;
    const ax = a % n, ay = Math.floor(a / n);
    const bx = b % n, by = Math.floor(b / n);
    return Math.abs(ax - bx) + Math.abs(ay - by) === 1;
  }

  private adjacentIndices(index: number): number[] {
    const n = this.state.n;
    const x = index % n;
    const y = Math.floor(index / n);
    const out: number[] = [];
    if (x > 0) out.push(index - 1);
    if (x < n - 1) out.push(index + 1);
    if (y > 0) out.push(index - n);
    if (y < n - 1) out.push(index + n);
    return out;
  }

  private swap(a: number, b: number) {
    const board = this.state.board;
    const t = board[a];
    board[a] = board[b];
    board[b] = t;
    if (board[a] === 0) this.state.blankIndex = a;
    else if (board[b] === 0) this.state.blankIndex = b;
  }
}

